// theme-tokens.js — shared theme constants for JS-side styling.
//
// One place, so a token isn't re-declared (and re-diverged) in every view.
// Cleanup 2026-07-15: `NAVY` was declared in 8 files and re-exported from
// App.jsx to 2 more, most of them carrying a raw hex fallback inside the
// `var()` — which (a) violated the tokens-only rule in spirit and (b) was
// redundant anyway, since `--th-navy` is defined in `public/theme.css`.
// The fallback is dropped; there is now one declaration, here.
//
// RULES.md rule 2/5: theme tokens only. Add a constant here rather than
// re-declaring one locally.

// INOVIU navy — panel headers, accents, primary buttons. (public/theme.css: --th-navy)
export const NAVY = 'var(--th-navy)';

// THE card shell — border/radius/background/padding for "one rectangle, title
// row inside it" (Alex, 2026-08-20). One declaration so HierarchyWorkspace's
// Spot and HeaderRow cannot drift the way two hand-typed copies of a border
// eventually do. OnshapeSection's own `frame` prop still carries its own copy
// (pre-dates this token, and not yet migrated) — a candidate to fold in later,
// not touched here to keep this pass to the surfaces Alex actually asked for.
//
// background is --th-row-even (white), NOT --th-panel-bg (#fafbfc) — Alex,
// 2026-08-20: "the DataGrid looks white... I would like inside the card to be
// white also." --th-panel-bg reads as a light blue/grey next to a grid's own
// white rows; --th-row-even is the token the grid itself already renders in,
// so the card's padding and the grid's body now share one background exactly.
//
// NO overflowX here, on purpose — it lived here for one round (2026-08-20) to
// contain a table wider than the card, then moved to DataGrid.jsx itself
// (wrapping just the table) the same day: Alex noticed the card's own
// overflow was scrolling the TITLE away along with the grid, since the title
// sat inside the same scrolling box. A grid's horizontal scroll belongs to
// the grid, not to whatever happens to frame it — the card stays a plain,
// non-scrolling box so its title row always stays put.
// The corner radius every rectangle-shaped surface shares: CARD_STYLE itself,
// and the three navy identity bars built to match its shape (App.jsx's shell
// header, OnshapePanel.jsx's embedded-panel header, OnshapeView.jsx's
// OnshapeDetailPanel bar) — Alex, 2026-08-21, asking whether the corner
// rounding was one constant before trying a larger fillet. It wasn't; this is
// that constant. Buttons have their OWN radius (--tg-editbtn-radius, below) —
// a deliberately separate value, not because it was left to drift, but
// because BTN_* now sweeps every button onto it on purpose (see below).
export const CARD_RADIUS = 4;

export const CARD_STYLE = {
  marginBottom: 12, padding: '8px 12px',
  border: '1px solid var(--th-border-subtle)', borderRadius: CARD_RADIUS,
  background: 'var(--th-row-even)',
};

// PANE_GAP — the ONE spacing value around every card-holding pane: the left
// aside's own outer padding, the gap between its stacked cards, and <main>'s
// own outer padding. Alex, 2026-08-20, comparing the tree-to-splitter gap
// against the splitter-to-content gap: "the dimension is great [on the left]
// ... the card is a little wider, almost three times [on the right] ... I
// would like all these spacings to be the same." Before this, the aside used
// 8px and <main> used '10px 14px' — two undeclared numbers that happened to
// differ. One constant now; a screen with its own `mainStyle` override (only
// CatalogView) must spread this in rather than retype a padding.
export const PANE_GAP = 8;

// BUTTON ROLES — one canonical style per semantic role (Alex, 2026-08-21: "the
// button in general. Height, radius, color based on what it is... same red
// when red... not slot look, only rectangle"). An audit found 83 buttons
// across 26 files, only ~19% using the existing --tg-editbtn-pad/radius
// tokens — everything else hand-rolled its own padding/radius/color,
// including a real bug: DataGrid's bulk-action-bar "Delete" trigger rendered
// GREY while the confirm button one click later rendered RED, same verb.
//
// Five roles, not one: a Save and a Delete are not the same button wearing
// different paint, so this is five constants, not one. Height and radius are
// the SAME token pair (--tg-editbtn-pad/radius) across the three boxed roles
// (primary/destructive/secondary) — the fillet Alex tried larger via
// CARD_RADIUS is a card-only rectangle, not this one, on purpose.
//
//   PRIMARY      — commits something (Save, Sync, Send, Link, Undo, Try again)
//   DESTRUCTIVE  — cannot be undone lightly (Delete confirm, the bulk trigger)
//   SECONDARY    — declines or steps back (Cancel, Go to Catalog, Reset, toggles)
//   GLYPH        — icon-only, inline, no box (row copy/delete/edit, ✕ dismiss)
//   TAB          — a toggle in a switcher/filter row (ViewSwitcher, status chips)
//
// A call site still layers its OWN disabled/active state on top by spreading
// this object first, e.g. `{...BTN_PRIMARY, ...(disabled ? BTN_DISABLED : null)}`
// — state is per-instance, the role's shape/color is not.
const BTN_BOX = {
  fontSize: 'var(--fs-small)', padding: 'var(--tg-editbtn-pad)', borderRadius: 'var(--tg-editbtn-radius)',
  cursor: 'pointer',
};
export const BTN_PRIMARY = {
  ...BTN_BOX,
  background: 'var(--tg-save-bg)', color: 'var(--tg-save-color)', border: 'var(--tg-save-border)',
};
export const BTN_DESTRUCTIVE = {
  ...BTN_BOX,
  background: 'var(--tg-delete-bg)', color: 'var(--th-row-even)', border: '1px solid var(--tg-delete-bg)',
};
// A destructive choice offered WITHOUT the weight of the primary action (e.g.
// ConfirmPanel's non-primary 'danger' tone, SyncWorklistSection's "Remove...
// gone" — a real remove, but one option among several, not a confirm step on
// its own). Same red as BTN_DESTRUCTIVE — "same red when red" holds even
// outline-vs-filled — just unfilled, so it doesn't out-weigh the primary
// button beside it.
export const BTN_DESTRUCTIVE_OUTLINE = {
  ...BTN_BOX,
  background: 'transparent', color: 'var(--tg-delete-bg)', border: '1px solid var(--tg-delete-bg)',
};
export const BTN_SECONDARY = {
  ...BTN_BOX,
  background: 'var(--tg-cancel-bg)', color: 'var(--tg-cancel-color)', border: 'var(--tg-cancel-border)',
};
// A primary action offered at a LIGHTER weight than BTN_PRIMARY — step 1 of a
// 2-step flow (OnshapeBomPanel's Prepare, before Load), or a repeatable action
// that shouldn't shout as loud as a one-time commit (OnshapeSyncReview's own
// "Sync from Onshape" / "Intentional"). Navy text/border carries the same
// brand as BTN_PRIMARY; unfilled is what says "lighter." NOT the same role as
// BTN_SECONDARY (grey) — this is still Primary's family, just not filled.
export const BTN_PRIMARY_OUTLINE = {
  ...BTN_BOX,
  background: 'var(--th-panel-bg)', color: NAVY, border: `1px solid ${NAVY}`,
};
// Disabled overlay for the three boxed roles — spread AFTER the role so it wins.
export const BTN_DISABLED = {
  background: 'var(--th-surface-disabled)', color: 'var(--th-text-disabled)',
  border: '1px solid var(--th-border-subtle)', cursor: 'default',
};
// No box at all — an icon standing in for a word, inline in a row (grid cells,
// dismiss ✕). Matches DataGrid's own row icons (copy/delete/edit, its highest-
// traffic source of these) — fs-body reads as a real glyph at a size that
// still fits a dense row; fs-small read as an afterthought next to its
// siblings. --tg-glyph-* exists for a HOVER/pressed treatment some of these
// want; this is the bare inline shape every one of them shares regardless.
export const BTN_GLYPH = {
  fontSize: 'var(--fs-body)', padding: '0 4px', border: 'none', background: 'none',
  color: 'var(--tg-icon-color)', cursor: 'pointer',
};
// A button sitting on the navy top banner (History). Translucent white fill and
// border so it reads on the banner in both themes; NOT a boxed role, which
// all assume a card surface.
export const BTN_BANNER = {
  fontSize: 'var(--fs-small)', fontFamily: 'inherit', padding: '3px 10px',
  border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--tg-editbtn-radius)',
  background: 'rgba(255,255,255,0.15)', color: 'var(--th-navy-text-muted)',
  cursor: 'pointer', whiteSpace: 'nowrap',
};
// A toggle inside a switcher/filter row — ViewSwitcher's top-level tabs,
// CatalogView's status chips. Only radius is genuinely shared: ViewSwitcher's
// tabs are deliberately heavier (fs-body, 4px 12px — the primary nav) than a
// dense filter-chip row (fs-small, 2px 8px, CatalogView 2026-08-03: already
// rectangular on purpose, "like the other ones... instead of oblong") — two
// real weights, not drift. Radius is the one value both already agreed on.
export const BTN_TAB_RADIUS = 4;

// Project status vocabulary — was declared identically in ProjectsView.jsx and
// ProjectsByCustomerView.jsx (2026-07-15 cleanup). RULES.md rule 3: `icon` +
// `word` carry the meaning; `color` is decoration only, never the sole signal.
export const PROJ_META = {
  quoting: { icon: '◷', word: 'Quoting', color: 'var(--th-text-secondary)' },
  won:     { icon: '✓', word: 'Won',     color: 'var(--th-status-synced)' },
  lost:    { icon: '✗', word: 'Lost',    color: 'var(--th-status-deprecated)' },
  active:  { icon: '▶', word: 'Active',  color: NAVY },
  closed:  { icon: '■', word: 'Closed',  color: 'var(--th-text-body)' },
};
